import { type GridRowSelectionModel, DataGridWithCheckbox, Flex, Text } from '@shared/components';
import { useMemo } from 'react';

import { useQuestionReviewTaskCreationFormContext } from '../../providers';

import AddQuestionListNoRowsOverlay from './add-question-list-no-rows-overlay';
import { type Question, columns } from './columns';
interface AddQuestionListProps {
  rows: Question[];
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export default function AddQuestionList(props: Readonly<AddQuestionListProps>) {
  const { rows, loading: isLoading, total, page, pageSize, onPageChange, onPageSizeChange } = props;
  const { setValue } = useQuestionReviewTaskCreationFormContext();
  const formContext = useQuestionReviewTaskCreationFormContext();
  const question = formContext.watch('question') ?? { questionId: {} };

  const selectedRowIds = useMemo(() => {
    return rows
      .filter((row) =>
        row.questionIds?.length
          ? row.questionIds.every((questionId) => question?.questionId?.[questionId])
          : question?.questionId?.[row.id],
      )
      .map((row) => row.id);
  }, [rows, question?.questionId]);

  const handleRowSelectionChange = (rowSelectionModel: GridRowSelectionModel) => {
    const selectedRowIds = new Set(rowSelectionModel as string[]);
    const questionId = rows.reduce(
      (acc, row) => {
        const rowQuestionIds = row.questionIds?.length ? row.questionIds : [row.id];

        if (selectedRowIds.has(row.id)) {
          rowQuestionIds.forEach((questionId) => {
            acc[questionId] = true;
          });
        } else {
          rowQuestionIds.forEach((questionId) => {
            delete acc[questionId];
          });
        }

        return acc;
      },
      { ...(question?.questionId ?? {}) } as Record<string, boolean | null | undefined>,
    );

    setValue('question', { questionId });
  };

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4), flex: 1 }}>
      <Text variant='h4'>เลือกแล้ว {Object.keys(question.questionId).length} ข้อ</Text>
      <DataGridWithCheckbox
        columns={columns}
        rows={rows}
        loading={isLoading}
        hideFooter={isLoading || total === 0}
        rowHeight={48}
        disableColumnMenu
        slots={{
          noRowsOverlay: isLoading ? () => null : AddQuestionListNoRowsOverlay,
        }}
        slotProps={{
          footer: {
            total: total,
            pagination: {
              page,
              count: total,
              rowsPerPage: pageSize,
              onPageChange: (_event, newPage) => {
                onPageChange(newPage);
              },
              onRowsPerPageChange: (event) => {
                onPageSizeChange(parseInt(event.target.value));
              },
            },
          },
        }}
        sx={(theme) => ({
          bgcolor: theme.palette.background.paper,
          '.MuiDataGrid-columnHeaders': {
            bgcolor: theme.palette.grey[100],
            borderColor: theme.palette.grey[300],
          },
          '& .MuiCheckbox-root': {
            color: theme.palette.grey[400],
            p: theme.spacing(3),
            '&.Mui-checked': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiDataGrid-row .MuiDataGrid-cell': {
            p: theme.spacing(3),
            '&.MuiDataGrid-cellCheckbox': {
              p: theme.spacing(0),
            },
          },
          '& .MuiDataGrid-cell, & .MuiDataGrid-row': {
            borderBottom: '1px solid',
            borderColor: theme.palette.grey[300],
          },
          border: '1px solid',
          borderColor: theme.palette.grey[300],
        })}
        rowSelectionModel={selectedRowIds}
        onRowSelectionModelChange={handleRowSelectionChange}
      />
    </Flex>
  );
}
